import { createGlobalStyle } from "styled-components";

export const supportDeviceSize = 1080;

export const GlobalStyle = createGlobalStyle`
/* 브랜드 로고 전용 폰트(영문). 인증카드·챌린지 워드마크에 쓴다. */
@font-face {
    font-family: "SuperDream";
    src: url("/fonts/SuperDream-Regular.ttf") format("truetype");
    font-weight: 400;
    font-display: swap;
}

:root {
    --color-main: #522CD4;
    --color-main-light: #5F3ADB;
    --color-main-second: #F3EFFF;
    --color-gauge: #CFD1FF;
    --color-gauge-transition: #9F8FEF;
    --color-black: #000000;
    --color-black-1: #1C1C1C;
    --color-white: #FFFFFF;
    --color-grey: #F9F9F9;
    --color-sub-warning: #DD5586;
    --color-sub-delay: #F7C92A;
    --color-sub-positive: #522CD4;
    --color-grey-990: #191919;
    --color-grey-900: #15191D;
    --color-grey-800: #2A323B;
    --color-grey-700: #3A4552;
    --color-grey-600: #666F7B;
    --color-grey-500: #8E9398;
    --color-grey-400: #AEB3B8;
    --color-grey-300: #D2D7DC;
    --color-grey-200: #E3E7ED;
    --color-grey-100: #EDF1F7;
    --color-grey-50: #F4F8FC;
    --color-grey-10: #FDFDFD;
    --color-lime-700: #BEDB0A;
    --color-lime-500: #DBF15F;
    --color-lime-400: #ECFA9B;
    --color-lime-100: #F9FFD7;
    --color-purple-800: #3C16C0;
    --color-purple-700: #522CD4;
    --color-purple-600: #6A46E7;
    --color-purple-500: #8A6BF5;
    --color-purple-400: #A88FFF;
    --color-purple-300: #BBA3FF;
    --color-purple-200: #CFD1FF;
    --color-purple-100: #F3EFFF;
    --color-toast: #FFFAEA;
    --color-error: #F93B3D;
    --color-blue: #1D68FF;
    --color-skyblue: #B7CEFF;
    --color-pink: #FDCAD7;

    /* --- Safe Area Top (SAT) ----------------------------------------
     * 2026-04-28 Hotfix — iOS WKWebView 는 contentInset 으로 이미 상단을 보정.
     * env(safe-area-inset-top) 이 그대로 남으면 이중 계산 → ImportFileScreen /
     * CreateBlankScreen 상단에 과도한 여백 발생 (Android 15 --sab 패턴과 동일).
     * html.ios-no-edge-inset 에서 --sat: 0px 으로 원복.
     * Android edge-to-edge 는 WebView 가 StatusBar 뒤로 연장 → env() 값 필요 → 유지. */
    --sat: env(safe-area-inset-top, 0px);

    /* --- Safe Area Bottom (SAB) -----------------------------------
     * 2026-04-19 Hotfix v1 — Android 15(targetSdk 35) edge-to-edge 회귀
     * 원인: WebView 가 네비바 뒤까지 확장 + env(safe-area-inset-bottom) 이
     * 네비바 높이(≈48dp) 만큼 리턴 → 기존 iOS Home Indicator 용 env() 규칙이
     * Android 에서도 적용되어 하단 요소 "붕 뜨는" 회귀.
     * 대응: html.aos-no-edge-inset { --sab: 0px } 로 원복.
     *
     * 2026-04-19 Hotfix v2 — iOS 동일 증상 재발 (대표 확인).
     * 원인 추정: WKWebView 의 contentInset / additionalSafeAreaInsets 가
     * 이미 Home Indicator 높이만큼 적용된 상태에서, CSS env(safe-area-inset-bottom)
     * 까지 더해져 "이중 계산" → Footer 가 Home Indicator 의 2배 높이로 뜸.
     * 특히 iPad Mini/iPad 류에서 더 심하게 체감 (대표 확인 스크린샷).
     * 대응: Android 와 동일한 패턴 — html.ios-no-edge-inset { --sab: 0px }
     * (DefaultLayout 에서 getUserAgent() === "apple" 일 때 부착).
     *
     * 최종 정식 수정(B안) — 네이티브(iOS WKWebView / Android WebView) 에서
     * contentInset 이중 계산을 제거. docs/android-edge-to-edge-native-fix.md
     * --------------------------------------------------------------- */
    --sab: env(safe-area-inset-bottom, 0px);
}

html.ios-no-edge-inset {
    /* iOS — WKWebView 가 contentInset 으로 상·하단 모두 보정.
     * CSS env() 가 더해지면 이중 계산 → 상단(--sat)·하단(--sab) 모두 0 으로 원복. */
    --sat: 0px;
    --sab: 0px;
}

html.aos-no-edge-inset {
    /* Android — 하단은 env(safe-area-inset-bottom) 으로 footer 를 네비게이션 바 위로
     * lift (2026-04-19 이전 = 4/14 안정 동작 복원). edge-to-edge 에서 env() 는
     * 네비바 높이를 단일 소스로 리턴(iOS 와 달리 이중 계산 없음) → footer 가 nav 바로 위에
     * 얹혀 탭 가능. --sab=0 강제 hotfix(+button-nav 휴리스틱)는 button-nav 사용자
     * footer 가림 회귀를 유발해 철회. 영구 fix 는 네이티브 edge-to-edge opt-out
     * (docs/android-edge-to-edge-native-fix.md).
     * --sat 은 0px 유지 — fitsSystemWindows=true 가 이미 status bar 보정 (top 동작 불변). */
    --sat: 0px;
    --sab: env(safe-area-inset-bottom, 0px);
}

html {
font-size: 10px; // 1rem = 10px 로 변경 한 것
// 참고링크 = https://stackoverflow.com/questions/68790660/setting-root-font-size-not-affecting-rem-units-in-safari-for-margin-padding-et
// 128px => 12.8rem , 4px => 0.4rem 가능!
-webkit-text-size-adjust: none;
-moz-text-size-adjust: none;
-ms-text-size-adjust: none;
}

html,
body {
    position: fixed;
    overflow: hidden;
}

/* 2026-04-28 — 페이지 전환 부드러운 페이드인.
 * 각 라우트의 main 요소가 마운트될 때 자동 트리거 (key 가 라우트마다 다르기 때문).
 * opacity 만 살짝 (0.92 → 1) → background show-through 최소화.
 * translateY 6px → 0 으로 미세한 위→아래 슬라이드 — Apple 스타일.
 * prefers-reduced-motion 사용자는 애니메이션 비활성. */
@keyframes pageFadeIn {
    from {
        opacity: 0.92;
        transform: translateY(6px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

body {
    width: 100dvw;
    margin: 0 auto;
    min-height: 100dvh;
    /* 2026-04-28 — 초기 페인트는 보라 (index.html 인라인 스타일).
     * 스플래시 진입 시 흰 번쩍임 제거. JS 마운트 후 DefaultLayout 의 useLayoutEffect
     * 가 pathname 따라 white/purple 동적 적용 (스플래시만 보라, 나머지 흰색). */
    background: var(--color-main);
    line-height: 1.4;
    letter-spacing: 0.006em;
    display: flex;
    flex-direction: column;
    align-items: center;
    color: var(--color-grey-900);

    #root {
        overflow-y: visible;
        -webkit-overflow-scrolling: touch;

        /* 2026-08-15 글자 크기 설정(fontScale) 안전망.
         * body 가 column flex 라 #root 는 flex 자식이고, 기본 min-width: auto 때문에
         * 폭이 '내용 최소 폭'까지 밀린다. 글자를 키우면 이 최소 폭이 화면보다 커져
         * 페이지 전체가 좌우로 밀린다(헤더·탭바까지 어긋남).
         * 화면 폭으로 묶어 두면 넘치는 건 안쪽에서만 처리된다.
         * 기본 크기에서는 넘치는 화면이 없어 보이는 결과가 같다. */
        min-width: 0;
        max-width: 100%;
    }

    main {

        /* 2026-08-15 글자 크기 설정(fontScale) 안전망.
         * body 가 column flex + align-items: center 라 main 의 폭은 '내용에 맞춰'
         * 정해진다. 안에 줄바꿈 불가 텍스트가 하나라도 있으면 글자를 키웠을 때
         * main 이 화면보다 넓어지면서 페이지 전체가 옆으로 밀린다.
         * min-width: 0 을 같이 주는 이유: flex 자식의 기본 min-width 는 auto(=내용
         * 최소 폭)라 max-width 보다 우선한다. 0 으로 낮춰야 상한이 실제로 걸린다. */
        max-width: 100%;
        min-width: 0;
        overflow: scroll;
        height: calc(100dvh - 4.6rem);
        -ms-overflow-style: none;
        scrollbar-width: none;

        &::-webkit-scrollbar {
            display: none;
        }
    }

    /* 2026-08-15 글자 크기 설정(fontScale) 안전망 2.
     * input 의 기본 너비는 "글자 20자쯤"이라 글자 크기에 비례해 같이 넓어진다.
     * flex 자식의 기본 min-width 는 auto(=그 너비)라, 글자를 키우면 입력창이
     * 부모를 밀어 화면 전체가 옆으로 밀린다(덱 만들기 화면에서 실측 419px/375px).
     * 0 으로 낮추면 화면 폭에 맞춰 줄어든다. 기본 크기에서는 이미 다 들어가서
     * 보이는 결과가 같다. */
    input,
    textarea,
    select {
        min-width: 0;
    }

    /* 2026-04-28 — 페이지 전환 페이드인.
     * main 자체가 아닌 main 의 직계 자식에 적용 → wallpaper 는 .wallpaper 클래스로
     * 제외(고정), .scrollable / 콘텐츠만 살짝 떠오름.
     * wallpaper 없는 페이지(통계/마이페이지)는 모든 자식이 떠올라 동일 효과. */
    main > *:not(.wallpaper) {
        animation: pageFadeIn 0.28s cubic-bezier(0.25, 0.1, 0.25, 1);

        @media (prefers-reduced-motion: reduce) {
            animation: none;
        }
    }

    /* 2026-05-25 — Pretendard 는 한글+라틴 전용 (CJK 한자 미포함).
       중국어 카드(简体/繁體) 렌더링 시 글자마다 OS 폴백 다르게 잡혀 두께·획
       들쭉날쭉. Pretendard 다음에 CJK 폰트 명시해 일관된 폴백 강제.
       - Noto Sans SC/TC: Android/Linux 기본 (Google 폰트)
       - PingFang SC/TC: iOS/macOS 시스템 CJK
       - Hiragino Sans GB: macOS legacy
       - Microsoft YaHei: Windows
       - "Noto Sans CJK SC" alias: 일부 안드로이드 빌드
       모두 시스템 설치본 사용 (외부 폰트 로드 없음 = 추가 비용 0). */
    * {
        font-family: "Pretendard Variable", Pretendard, -apple-system, BlinkMacSystemFont, system-ui, Roboto, "Helvetica Neue", "Segoe UI", "Apple SD Gothic Neo", "Noto Sans KR", "Malgun Gothic", "Noto Sans SC", "Noto Sans TC", "Noto Sans CJK SC", "PingFang SC", "PingFang TC", "Hiragino Sans GB", "Microsoft YaHei", "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", sans-serif;
        border: none;
        outline: none;
        padding: 0;
        margin: 0;
        background: transparent;
        box-sizing: border-box;
        -webkit-tap-highlight-color: transparent;
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
        -webkit-focus-ring-color: transparent;
    }

    h1 {
        font-size: 2.8rem;
        letter-spacing: 0;
    }

    h2 {
        font-size: 2.4rem;
        letter-spacing: 0;
    }

    h3 {
        font-size: 2rem;
        font-weight: 600;
        letter-spacing: 0;
    }

    button {
        cursor: pointer;
    }

    img {
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-user-drag: none;
        touch-action: manipulation;
    }
}
`;
